import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { t } from 'i18next';
import { PiNavigationArrow, PiPlus, PiTrash } from 'react-icons/pi';
import { IClientAccountForm } from 'services/client-account/types';
import { useGetMusicProductDigitalLightList } from 'services/music-product-digital/light';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

const ClientAccountDigitalMusicProducts = () => {
  const digitalMusicProductsLightList = useGetMusicProductDigitalLightList();

  const { values, setFieldValue, status, initialStatus } =
    useFormikContext<IClientAccountForm>();

  const navigateToNewWindow = useNewWindowNavigate();

  const digitalMusicProductOptions = (digitalMusicProducts: string[]) =>
    digitalMusicProductsLightList.data
      ?.filter(
        digitalMusicProduct =>
          !digitalMusicProducts.includes(digitalMusicProduct.id)
      )
      .map(digitalMusicProduct => ({
        label: `${digitalMusicProduct.mainTitle.title ?? ''} - ${
          digitalMusicProduct.mainTitle.version ?? ''
        } (${digitalMusicProduct.ean})`,
        value: digitalMusicProduct.id,
      })) ?? [];

  const goToDigitalMusicProduct = (id: string) => {
    navigateToNewWindow(`music-product-digital/${id}`);
  };

  return (
    <Table
      displayMode
      data={{
        nodes:
          values.digitalMusicProducts?.map((digitalMusicProduct, key) => ({
            id: key,
            value: digitalMusicProduct.id,
            label: `${digitalMusicProduct.name ?? ''} (${
              digitalMusicProduct.ean
            })`,
          })) ?? [],
      }}
      columns={[
        {
          label: t('input.label.music-product-digital'),
          renderCell: ({ label, value, id }) => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text text={label} />
              ) : (
                <FormInput.Select
                  name={`digitalMusicProducts[${id}].id`}
                  label={t('input.label.music-product-digital')}
                  options={digitalMusicProductOptions(
                    values.digitalMusicProducts?.map(
                      digitalMusicProduct => digitalMusicProduct.id
                    )
                  )}
                  onChange={option => {
                    setFieldValue(
                      `digitalMusicProducts[${id}].id`,
                      option.value
                    );
                    setFieldValue(
                      `digitalMusicProducts[${id}].name`,
                      option.label
                    );
                  }}
                  value={{ label: label ?? '-', value: value }}
                />
              )}
            </TableItem>
          ),
        },
        {
          label: '',
          renderCell: ({ value }) => (
            <TableItem justifyPosition='end'>
              {status?.displayMode ? (
                <Button
                  isClear
                  onlyIcon
                  onClick={() => goToDigitalMusicProduct(value)}>
                                    <Button.Description description={t('button.open')} />

                                    <Button.Icon
                    icon={<PiNavigationArrow transform='scale(-1,1)' />}
                  />
                </Button>
              ) : (
                <Button
                  variant={BUTTON_VARIANT.remove}
                  isClear
                  onlyIcon
                  onClick={() =>
                    setFieldValue(
                      `digitalMusicProducts`,
                      values.digitalMusicProducts?.filter(
                        digitalMusicProduct => digitalMusicProduct.id !== value
                      )
                    )
                  }>
                                    <Button.Description description={t('button.remove')} />

                                    <Button.Icon icon={<PiTrash />} />
                </Button>
              )}
            </TableItem>
          ),
        },
      ]}
      tableOptions={
        status?.displayMode
          ? undefined
          : {
              renderAfterTable: () => (
                <tfoot className='full-width-footer'>
                  <TableMessage
                    className='cursor-pointer'
                    disable={
                      values.digitalMusicProducts?.length
                        ? !values.digitalMusicProducts[
                            values.digitalMusicProducts.length - 1
                          ]?.id.length
                        : false
                    }
                    onClick={() =>
                      setFieldValue(
                        `digitalMusicProducts[${
                          values.digitalMusicProducts?.length ?? 0
                        }]`,
                        {
                          id: '',
                          name: '',
                        }
                      )
                    }>
                    <TableItem.Text text={t('button.add')} />
                    <PiPlus />
                  </TableMessage>
                </tfoot>
              ),
            }
      }
      size='compact'
    />
  );
};

export default ClientAccountDigitalMusicProducts;
